<template>
  <v-row no-gutters>
    <v-col cols="12" lg="8" md="8">
      <!-- Left column -->
      <p class="text-h2">
        {{ canEditOffer ? $t('STEPPER_STEP4_TITLE') : $t('STEPPER_STEP4_LABEL') }}
      </p>
      <p v-if="canEditOffer" class="text-body-1 grey-font">
        <span class="bold">
          {{ $t('STEPPER_STEP4_DESC1', { offerType }) }}
        </span>
        {{ $t('STEPPER_STEP4_DESC2') }}
      </p>
      <p class="text-h3 mt-8 grey-font">{{ $t('STEPPER_STEP4_SUBTITLE') }}</p>

      <v-form>
        <!-- Package amount -->
        <v-text-field
          class="packages__textfield text-body-2"
          :label="$t('STEPPER_STEP4_TEXTFIELD1')"
          v-model.number="packageAmount"
          :rules="[rules.required, rules.number]"
          required
          outlined
          :readonly="!canEditOffer"
          @focus="!canEditOffer && $event.target.blur()"
        />
        <!-- Package amount end -->

        <!-- Goods types -->
        <v-text-field
          v-if="!canEditOffer"
          readonly
          outlined
          class="packages__textfield text-body-2"
          :label="$t('STEPPER_STEP4_TEXTFIELD2')"
          :value="$t(goodTypesSelectedItemDescription)"
          @focus="$event.target.blur()"
        />
        <v-select
          v-else
          required
          outlined
          class="packages__textfield text-body-2"
          :label="$t('STEPPER_STEP4_TEXTFIELD2')"
          :rules="[rules.required]"
          :items="goodTypesSelectItems"
          item-text="desc_key"
          item-value="id"
          v-model="goodTypesSelectedItem"
        />
        <!-- Goods types end -->
      </v-form>

      <!-- Packages table -->
      <v-data-table
        hide-default-footer
        disable-pagination
        class="elevation-0"
        :mobile-breakpoint="0"
        :headers="headers"
        :items="typeOfPackages"
      >
        <!-- Packages table: item modal -->
        <template v-slot:header.name>
          <span v-if="!canEditOffer">{{ headers[0].text }}</span>
          <v-dialog v-else v-model="typeOfPackageDialog" max-width="500px">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                text
                small
                color="grey"
                class="text-body-2 pr-0 pl-0 non-text-transformation display-btn"
                v-bind="attrs"
                v-on="on"
              >
                {{ headers[0].text }}
                <v-icon class="text-h3 pl-1">mdi-plus</v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-card-title>
                <span class="text-h3">{{ $t('STEPPER_STEP4_TABLE_DIALOG_TITLE') }}</span>
              </v-card-title>
              <v-form ref="typeOfPackageForm">
                <v-card-text class="pb-0">
                  <v-container>
                    <v-row>
                      <v-col
                        v-for="(header, key) in headers"
                        :key="'header-' + key"
                        cols="12"
                        sm="6"
                        md="4"
                      >
                        <v-text-field
                          v-if="header.value !== 'actions'"
                          outlined
                          v-model="editedItem[header.value]"
                          :rules="key === 0 ? [rules.required] : [rules.required, rules.number]"
                          :label="header.text"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
              </v-form>
              <v-card-actions class="pr-9">
                <v-spacer />
                <v-btn
                  text
                  tile
                  large
                  color="primary"
                  class="link non-text-transformation"
                  @click="handleCloseTypeOfPackageDialog"
                >
                  {{ $t('STEPPER_STEP4_TABLE_DIALOG_BUTTON_CLOSE') }}
                </v-btn>
                <v-btn
                  class="link non-text-transformation"
                  tile
                  large
                  color="primary"
                  @click="handleSaveTypeOfPackage"
                  :disabled="!isValidTypeOfPackage(editedItem)"
                >
                  {{ $t('STEPPER_STEP4_TABLE_DIALOG_BUTTON_SAVE') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
        <!-- Packages table: item modal end -->

        <!-- Packages table: body -->
        <template v-slot:body="{ items, headers }">
          <tbody>
            <tr v-for="(item, idx) in items" :key="'row-' + idx">
              <td
                v-for="(header, key) in headers"
                :key="'prop-' + key"
                :class="typeOfPackageClass(item, header)"
              >
                <div v-if="!canEditOffer">
                  {{ typeOfPackageItemValue(item[header.value], header.value) }}
                </div>
                <div v-else>
                  <span v-if="header.value !== 'actions'">
                    {{ typeOfPackageItemValue(item[header.value], header.value) }}
                  </span>
                  <div v-else class="d-flex">
                    <v-btn
                      text
                      small
                      color="grey"
                      class="text-body-2 pr-0 pl-0 non-text-transformation display-btn"
                      @click="handleEditTypeOfPackage(item, idx)"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-dialog v-model="deleteTypeOfPackageDialog" width="500">
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on" small>mdi-delete</v-icon>
                      </template>
                      <v-card>
                        <v-card-title class="text-h3">
                          {{ $t('STEPPER_STEP4_DELETE_MODAL_TITLE') }}
                        </v-card-title>
                        <v-card-text class="text-body-2 mt-4 mb-4"
                          >{{ $t('STEPPER_STEP4_DELETE_TABLE_ITEM') }}
                        </v-card-text>
                        <v-card-actions class="pb-4 pr-8">
                          <v-spacer />
                          <v-btn
                            class="link non-text-transformation"
                            color="black"
                            outlined
                            tile
                            @click="handleCloseDeleteTypeOfPackageDialog"
                          >
                            {{ $t('UNLOCK_DISCOUNTS_MODAL_CANCEL') }}
                          </v-btn>
                          <v-btn
                            class="link non-text-transformation"
                            color="primary"
                            tile
                            @click="handleDeleteTypeOfPackage(idx)"
                          >
                            {{ $t('STEPPER_STEP4_DELETE_BUTTON') }}
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-dialog>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td
                class="packages__totals text-end"
                v-for="(header, key) in headers"
                :key="'prop-' + key"
              >
                <span v-if="key === headers.length - 3">
                  {{ $t('STEPPER_STEP4_TABLE_TOTAL') }}
                </span>
                <span :class="colorTotalPercentage" v-if="key === headers.length - 2">
                  {{ totalPercentage }}%
                </span>
              </td>
            </tr>
          </tbody>
        </template>
        <!-- Packages table: body end -->
      </v-data-table>

      <!-- Packages errors -->
      <div v-if="typeOfPackagesError" class="v-messages theme--light error--text mt-2" role="alert">
        <div class="v-messages__wrapper">
          <div class="text-caption v-messages__message">
            {{ $t('STEPPER_STEP4_ERROR_MESSAGE') }}
          </div>
        </div>
      </div>
      <div v-if="hasPercentageErrors" class="v-messages theme--light error--text mt-2" role="alert">
        <div class="v-messages__wrapper">
          <div class="text-caption v-messages__message">
            {{ $t('STEPPER_STEP4_EXCEEDED_ERROR_MESSAGE') }}
          </div>
        </div>
      </div>
      <!-- Packages errors end -->
      <!-- Packages table end -->

      <!-- Refunds -->
      <v-form ref="refundsForm">
        <p class="text-h3 mt-8 grey-font">
          {{ $t('STEPPER_STEP4_SUBTITLE2') }}
        </p>
        <div class="d-flex">
          <v-text-field
            required
            outlined
            :readonly="!canEditOffer"
            :label="$t('STEPPER_STEP4_TEXTFIELD3')"
            class="packages__textfield mr-8 text-body-2"
            v-model="numberShipmentRepayments"
            :rules="[
              rules.refundsRequired,
              rules.refundsNumber,
              rules.numberShipmentRepayments,
              rules.commitedDeliveries,
            ]"
            @keyup="$refs.refundsForm.validate()"
            @focus="!canEditOffer && $event.target.blur()"
          />
          <v-text-field
            required
            outlined
            :readonly="!canEditOffer"
            :label="$t('STEPPER_STEP4_TEXTFIELD4')"
            class="packages__textfield text-body-2"
            v-model="meanRepaymentValue"
            :rules="[rules.refundsRequired, rules.refundsNumber, rules.meanRepaymentValue]"
            @keyup="$refs.refundsForm.validate()"
            @focus="!canEditOffer && $event.target.blur()"
          />
        </div>
        <!-- Refunds end -->

        <!-- Pick-up days -->
        <p class="text-h4 mt-2 mb-0 grey-font">
          {{ $t('STEPPER_STEP4_SUBTITLE4') }}
        </p>
        <v-checkbox
          v-if="canEditOffer"
          hide-details
          class="pl-2 mt-0 mb-3 text-h4 bold"
          :label="$t('STEPPER_STEP4_CHIP_MONDAY_TO_FRIDAY')"
          v-model="isMondayToFridaySelected"
          @click="handleModayToFridayClick()"
        />
        <template v-if="canEditOffer">
          <v-chip
            v-for="(day, index) in daysOfWeekItems"
            :key="day + '-' + index"
            color="primary"
            :class="`mt-3 ${getDayOfWeekClass(index)}`"
            :outlined="!isDayOfWeekSelected(day)"
            :disabled="isMondayToFridaySelected"
            @click="handleDayOfWeekClick(day, index)"
          >
            <v-icon class="pr-1 text-body-1" v-if="isDayOfWeekSelected(day)">mdi-check</v-icon>
            {{ $t(day.desc) }}
          </v-chip>
        </template>
        <template v-else>
          <v-chip
            v-for="(day, index) in daysOfWeekItems"
            :key="day + '-' + index"
            :outlined="!isDayOfWeekSelected(day)"
            :class="`mt-3 ${getDayOfWeekClass(index)}`"
            color="primary"
          >
            <v-icon class="pr-1 text-body-1" v-if="isDayOfWeekSelected(day)">mdi-check</v-icon>
            {{ $t(day.desc) }}
          </v-chip>
        </template>

        <!-- Pick-up days errors -->
        <div v-if="daysOfWeekError" class="v-messages theme--light error--text mt-2" role="alert">
          <div class="v-messages__wrapper">
            <div class="text-caption v-messages__message">
              {{ $t('STEPPER_STEP4_DAYS_OF_WEEK_REQUIRED') }}
            </div>
          </div>
        </div>
        <!-- Pick-up days errors end -->
        <!-- Pick-up days end -->

        <!-- Observations -->
        <p class="text-h3 mt-10 grey-font">{{ $t('STEPPER_STEP4_SUBTITLE3') }}</p>
        <v-textarea
          outlined
          :rules="[rules.observations]"
          :label="canEditOffer ? $t('STEPPER_STEP4_OBSERVATION_LABEL') : ''"
          v-model="observations"
          :readonly="!canEditOffer"
          @focus="!canEditOffer && $event.target.blur()"
        />
        <!-- Observations end -->
        <!-- Left column end -->
      </v-form>
    </v-col>

    <SummaryCard>
      <template v-slot:save-and-reset-offer-buttons>
        <SaveAndResetOfferButtons @onSaveClicked="saveOffer" @onResetClicked="resetOffer" />
      </template>

      <template v-slot:offer-status-button>
        <UpdateOfferStatusButton />
      </template>

      <template v-slot:export-excel-button>
        <ExportToExcelButton />
      </template>
    </SummaryCard>
  </v-row>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { GET_OFFER_ITEM } from '@/store/modules/offer/getters'
import { UPDATE_PACKAGE_ITEM } from '@/store/modules/offer/actions'
import SummaryCard from '@/components/summary-card'
import SaveAndResetOfferButtons from '@/components/buttons/SaveAndResetOfferButtons'
import UpdateOfferStatusButton from '@/components/buttons/UpdateOfferStatusButton'
import ExportToExcelButton from '@/components/buttons/ExportToExcelButton'

export default {
  name: 'Packages',
  components: {
    SummaryCard,
    SaveAndResetOfferButtons,
    UpdateOfferStatusButton,
    ExportToExcelButton,
  },
  data() {
    return {
      typeOfPackageDialog: false,
      deleteTypeOfPackageDialog: false,
      daysOfWeekNames: [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
      ],
      headers: [
        {
          text: this.$t('STEPPER_STEP4_HEADER_TYPE'),
          align: 'start',
          sortable: false,
          value: 'name',
          class: 'text-body-2',
          width: '15%',
        },
        {
          text: this.$t('STEPPER_STEP4_HEADER_WEIGHT'),
          value: 'Kg',
          sortable: false,
          align: 'end',
          class: 'text-body-2',
          width: '15%',
        },
        {
          text: this.$t('STEPPER_STEP4_HEADER_LONG'),
          value: 'Long',
          sortable: false,
          align: 'end',
          class: 'text-body-2',
          width: '15%',
        },
        {
          text: this.$t('STEPPER_STEP4_HEADER_WIDTH'),
          value: 'Width',
          sortable: false,
          align: 'end',
          class: 'text-body-2',
          width: '15%',
        },
        {
          text: this.$t('STEPPER_STEP4_HEADER_HEIGHT'),
          value: 'Height',
          sortable: false,
          align: 'end',
          class: 'text-body-2',
          width: '15%',
        },
        {
          text: this.$t('STEPPER_STEP4_HEADER_PERCENTAGE'),
          value: 'percentage',
          sortable: false,
          align: 'end',
          class: 'text-body-2',
          width: '15%',
        },
        {
          text: '',
          value: 'actions',
          sortable: false,
          align: 'end',
          class: 'text-body-2',
        },
      ],
      tableContent: [],
      editedItem: {
        name: '',
        Kg: 0,
        Long: 0,
        Width: 0,
        Height: 0,
        percentage: 0,
      },
      defaultItem: {
        name: '',
        Kg: 0,
        Long: 0,
        Width: 0,
        Height: 0,
        percentage: 0,
      },
      editedIndex: -1,
      rules: {
        required: value => !!value || this.$t('STEPPER_STEP4_ERROR_MESSAGE'),
        number: value => this.isNumeric(value) || this.$t('STEPPER_STEP4_NUMBER_ERROR_MESSAGE'),
        refundsRequired: value => value !== '' || this.$t('STEPPER_STEP4_ERROR_MESSAGE'),
        refundsNumber: value =>
          (!isNaN(this.getNumericValue(value)) && this.getNumericValue(value) >= 0) ||
          this.$t('STEPPER_STEP4_NUMBER_ERROR_MESSAGE'),
        numberShipmentRepayments: value =>
          this.refundsValidation('numberShipmentRepayments', value) ||
          this.$t('STEPPER_STEP4_NUMBER_NUMBEROFREFUNDS_0'),
        meanRepaymentValue: value =>
          this.refundsValidation('meanRepaymentValue', value) ||
          this.$t('STEPPER_STEP4_REFUNDS_VALUE_0'),
        commitedDeliveries: value =>
          value <= this.commitedDeliveries || this.$t('STEPPER_STEP4_COMMITED_DELIVERIES_ERROR'),
        observations: value =>
          value.length <= 350 || this.$t('STEPPER_STEP4_OBSERVATION_ERROR_MESSAGE'),
      },
      // TODO: set typeOfPackagesError to true (when? if something below the table is dirty?)
      typeOfPackagesError: false,
      daysOfWeekError: false,
      isMondayToFridaySelected: false,
    }
  },
  computed: {
    ...mapGetters('offer', [GET_OFFER_ITEM, 'canEditOffer']),
    offerType() {
      return this[GET_OFFER_ITEM]('type')
    },
    additionalServices() {
      return this[GET_OFFER_ITEM]('additionalServices')
    },
    packageAmount: {
      get() {
        const { packageAmount } = this.additionalServices
        return packageAmount || ''
      },
      set(value) {
        this.setPackageAmount(value)
      },
    },
    goodTypes() {
      return this.additionalServices.goodTypes
    },
    goodTypesSelectItems() {
      return (
        this.goodTypes &&
        this.goodTypes.map(element => ({
          id: element.id,
          desc_key: this.$t(element.desc_key),
        }))
      )
    },
    goodTypesSelectedItem: {
      get() {
        return this.goodTypes?.find(element => element.isSelected)?.id
      },
      set(value) {
        this.setGoodTypesSelectedItem(value)
      },
    },
    goodTypesSelectedItemDescription() {
      return this.goodTypes?.find(element => element.isSelected)?.desc_key || '-'
    },
    numberShipmentRepayments: {
      get() {
        return this.additionalServices.numberShipmentRepayments
      },
      set(value) {
        this.setNumberShipmentRepayments(value)
      },
    },
    meanRepaymentValue: {
      get() {
        return this.additionalServices.meanRepaymentValue
      },
      set(value) {
        this.setMeanRepaymentValue(value)
      },
    },
    pickupDays() {
      return this.additionalServices.pickupDays
    },
    daysOfWeekItems() {
      return this.daysOfWeekNames.map(dayName => ({
        desc: `STEPPER_STEP4_CHIP_${dayName.toUpperCase()}`,
        value: dayName,
      }))
    },
    observations: {
      get() {
        return this.additionalServices.observations
      },
      set(value) {
        this.setObservations(value)
      },
    },
    commitedDeliveries() {
      return Number(this[GET_OFFER_ITEM]('clientCommitedDeliveries'))
    },
    typeOfPackages() {
      return this.additionalServices.typeOfPackages
    },
    totalPercentage() {
      const percentageSum = this.typeOfPackages
        .map(element => Number(element.percentage))
        .reduce((a, b) => a + b, 0)

      return this.getNumericValue(percentageSum * 100)
    },
    hasPercentageErrors() {
      return this.typeOfPackages.length && this.totalPercentage !== 100
    },
    colorTotalPercentage() {
      return this.hasPercentageErrors ? 'primary--text' : null
    },
    isFormValid() {
      return (
        this.isNumeric(this.packageAmount) &&
        this.goodTypes.find(item => item.isSelected) &&
        this.typeOfPackages.length > 0 &&
        this.typeOfPackages.every(item => this.isValidTypeOfPackage(item)) &&
        !this.typeOfPackagesError &&
        !this.hasPercentageErrors &&
        ((this.numberShipmentRepayments === 0 && this.meanRepaymentValue === 0) ||
          (this.isNumeric(this.numberShipmentRepayments) &&
            this.isNumeric(this.meanRepaymentValue))) &&
        this.pickupDays.totalDays > 0
      )
    },
  },
  methods: {
    ...mapActions('offer', [UPDATE_PACKAGE_ITEM]),
    ...mapMutations('process', ['setIsPackagesFormValid']),
    updatePackageItems(data) {
      this[UPDATE_PACKAGE_ITEM](data)
    },
    isNumeric(num) {
      return !isNaN(this.getNumericValue(num)) && this.getNumericValue(num) > 0
    },
    getNumericValue(num) {
      const parsedNum = typeof num === 'string' ? Number(num.replace(',', '.')) : num
      return Number(parsedNum.toFixed(2))
    },
    setPackageAmount(value) {
      this.updatePackageItems({ packageAmount: this.getNumericValue(value) })
    },
    setGoodTypesSelectedItem(value) {
      const goodTypes = this.goodTypes.map(element => {
        element.isSelected = element.id === value
        return element
      })
      this.updatePackageItems({ goodTypes })
    },
    typeOfPackageItemValue(value, field) {
      return field === 'percentage' ? `${this.getNumericValue(value * 100)}%` : value
    },
    setTypeOfPackages(value) {
      this.updatePackageItems({ typeOfPackages: value })
    },
    handleEditTypeOfPackage(item, index) {
      this.editedItem = { ...item }
      this.editedItem.percentage = this.getNumericValue(item.percentage * 100)
      this.editedIndex = index
      this.typeOfPackageDialog = true
    },
    handleCloseTypeOfPackageDialog() {
      this.typeOfPackageDialog = false
    },
    isValidTypeOfPackage(item) {
      for (let element in item) {
        if (element === 'name') {
          if (item[element] === '') {
            return false
          }
        } else if (!this.isNumeric(item[element])) {
          return false
        }
      }
      return true
    },
    handleSaveTypeOfPackage() {
      if (this.isValidTypeOfPackage(this.editedItem)) {
        this.editedItem.Kg = this.getNumericValue(this.editedItem.Kg)
        this.editedItem.Long = this.getNumericValue(this.editedItem.Long)
        this.editedItem.Width = this.getNumericValue(this.editedItem.Width)
        this.editedItem.Height = this.getNumericValue(this.editedItem.Height)
        this.editedItem.percentage = this.getNumericValue(this.editedItem.percentage / 100)
        const typeOfPackages = [...this.typeOfPackages]
        if (this.editedIndex > -1) {
          Object.assign(typeOfPackages[this.editedIndex], this.editedItem)
        } else {
          typeOfPackages.push(this.editedItem)
        }
        this.setTypeOfPackages(typeOfPackages)
        this.typeOfPackagesError = false
      }
      this.handleCloseTypeOfPackageDialog()
    },
    handleCloseDeleteTypeOfPackageDialog() {
      this.deleteTypeOfPackageDialog = false
    },
    handleDeleteTypeOfPackage(index) {
      const typeOfPackages = [...this.typeOfPackages]
      typeOfPackages.splice(index, 1)
      this.setTypeOfPackages(typeOfPackages)
      this.handleCloseDeleteTypeOfPackageDialog()
      if (!typeOfPackages.length) {
        this.typeOfPackagesError = true
      }
    },
    typeOfPackageClass(item, header) {
      let classes = header.value === 'name' ? 'text-start' : 'text-end'
      if (
        header.value !== 'name' &&
        header.value !== 'actions' &&
        !this.isNumeric(item[header.value])
      ) {
        classes += ' packages__error'
      }
      return classes
    },
    refundsValidation(type) {
      if (type === 'numberShipmentRepayments') {
        return Number(this.meanRepaymentValue) === 0
          ? Number(this.numberShipmentRepayments) === 0
          : true
      } else {
        return Number(this.numberShipmentRepayments) === 0
          ? Number(this.meanRepaymentValue) === 0
          : true
      }
    },
    setNumberShipmentRepayments(value) {
      this.updatePackageItems({ numberShipmentRepayments: this.getNumericValue(value) })
    },
    setMeanRepaymentValue(value) {
      this.updatePackageItems({ meanRepaymentValue: this.getNumericValue(value) })
    },
    getDayOfWeekClass(index) {
      if (index === 0) return 'mr-2'
      if (index === 6) return 'ml-2'
      return 'mx-2'
    },
    isDayOfWeekSelected(day) {
      return this.pickupDays.dayList.includes(day.value)
    },
    daysOfWeekSorter(a, b) {
      return this.daysOfWeekNames.indexOf(a) - this.daysOfWeekNames.indexOf(b)
    },
    handleDayOfWeekClick(day) {
      const pickupDays = {
        dayList: [...this.pickupDays.dayList],
      }

      if (this.isDayOfWeekSelected(day)) {
        const index = pickupDays.dayList.indexOf(day.value)
        pickupDays.dayList.splice(index, 1)
      } else {
        pickupDays.dayList.push(day.value)
        pickupDays.dayList.sort(this.daysOfWeekSorter)
      }

      pickupDays.totalDays = pickupDays.dayList.length

      this.updatePackageItems({ pickupDays })

      this.daysOfWeekError = pickupDays.totalDays === 0
    },
    handleModayToFridayClick() {
      const pickupDays = {
        totalDays: 0,
        dayList: [],
      }

      if (this.isMondayToFridaySelected) {
        pickupDays.totalDays = 5
        pickupDays.dayList = this.daysOfWeekNames.slice(0, 5)
      }

      this.updatePackageItems({ pickupDays })

      this.daysOfWeekError = pickupDays.totalDays === 0
    },
    setObservations(value) {
      this.updatePackageItems({ observations: value })
    },
    saveOffer() {
      this.$emit('onSaveClicked')
    },
    resetOffer() {
      this.$emit('onResetClicked')
    },
    formValidationChangeHandler() {
      this.$emit('onChangeIsContinueForbidden', !this.isFormValid)
      this.setIsPackagesFormValid(this.isFormValid)
    },
  },
  created() {
    const mondayToFridayNames = this.daysOfWeekNames.slice(0, 5)
    if (
      this.pickupDays.totalDays === 5 &&
      this.pickupDays.dayList
        .slice()
        .sort()
        .join() === mondayToFridayNames.sort().join()
    ) {
      this.isMondayToFridaySelected = true
    }

    this.formValidationChangeHandler()
  },
  watch: {
    isFormValid() {
      this.formValidationChangeHandler()
    },
    typeOfPackageDialog() {
      if (this.typeOfPackageDialog) {
        this.$refs.typeOfPackageForm?.resetValidation()
      } else {
        this.editedItem = { ...this.defaultItem }
        this.editedIndex = -1
      }
    },
  },
}
</script>

<style scoped lang="scss">
.packages {
  &__error {
    background-color: rgb(223, 0, 36, 0.12);
  }
  &__textfield {
    max-width: rem(360);
  }
  &__totals {
    background-color: $light-grey;
  }
  &__active-chip {
    background-color: $primary !important;
    color: white;
  }
}
</style>
